import React, { useState, useEffect } from 'react';
import axiosInstance from '../../api/axiosInstance';
import './AIRecruitment.css';
import {
  ChartBarIcon,
  BriefcaseIcon,
  NewspaperIcon,
  UsersIcon,
  EyeIcon,
  TrashIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import logger from '../../../utils/logger';

const AIRecruitment = ({ showNotification }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [hiringRequests, setHiringRequests] = useState([]);
  const [jobPostings, setJobPostings] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showCandidateModal, setShowCandidateModal] = useState(false);
  const [selectedHiringRequest, setSelectedHiringRequest] = useState(null);
  const [showHiringRequestModal, setShowHiringRequestModal] = useState(false);
  const [selectedJobPosting, setSelectedJobPosting] = useState(null);
  const [showJobPostingModal, setShowJobPostingModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'dashboard') {
        await Promise.all([
          fetchStats(),
          fetchRecentHiringRequests()
        ]);
      } else if (activeTab === 'hiring-requests') {
        await fetchHiringRequests();
      } else if (activeTab === 'job-postings') {
        await fetchJobPostings();
      } else if (activeTab === 'candidates') {
        await fetchCandidates();
      }
    } catch (error) {
      logger.error('Error fetching recruitment data:', error);
      showNotification?.('Failed to load recruitment data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axiosInstance.get('/api/recruitment/stats');

      if (response.data.success) {

        setStats(response.data.data);
      }
    } catch (error) {
      logger.error('Error fetching stats:', error);
    }
  };

  const fetchRecentHiringRequests = async () => {
    try {
      const response = await axiosInstance.get('/api/recruitment/hiring-requests?limit=5');

      if (response.data.success) {

        setHiringRequests(response.data.data);
      }
    } catch (error) {
      logger.error('Error fetching recent hiring requests:', error);
    }
  };

  const fetchHiringRequests = async () => {
    try {
      const response = await axiosInstance.get('/api/recruitment/hiring-requests');

      if (response.data.success) {

        setHiringRequests(response.data.data);
      }
    } catch (error) {
      logger.error('Error fetching hiring requests:', error);
    }
  };

  const fetchJobPostings = async () => {
    try {
      const response = await axiosInstance.get('/api/recruitment/job-postings');

      if (response.data.success) {

        setJobPostings(response.data.data);
      }
    } catch (error) {
      logger.error('Error fetching job postings:', error);
    }
  };

  const fetchCandidates = async () => {
    try {
      const response = await axiosInstance.get('/api/recruitment/candidates');

      if (response.data.success) {

        setCandidates(response.data.data);
      }
    } catch (error) {
      logger.error('Error fetching candidates:', error);
    }
  };

  const viewCandidateDetails = (candidate) => {
    setSelectedCandidate(candidate);
    setShowCandidateModal(true);
  };

  const viewHiringRequestDetails = async (requestId) => {
    try {
      const response = await axiosInstance.get(`/api/recruitment/hiring-requests/${requestId}`);
      if (response.data.success) {
        setSelectedHiringRequest(response.data.data);
        setShowHiringRequestModal(true);
      }
    } catch (error) {
      logger.error('Error fetching hiring request details:', error);
      showNotification?.('Failed to load hiring request details', 'error');
    }
  };

  const viewJobPostingDetails = async (postingId) => {
    try {
      const response = await axiosInstance.get(`/api/recruitment/job-postings/${postingId}`);
      if (response.data.success) {
        setSelectedJobPosting(response.data.data);
        setShowJobPostingModal(true);
      }
    } catch (error) {
      logger.error('Error fetching job posting details:', error);
      showNotification?.('Failed to load job posting details', 'error');
    }
  };

  const deleteHiringRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this hiring request?')) {
      return;
    }

    try {
      const response = await axiosInstance.delete(`/api/recruitment/hiring-requests/${requestId}`);
      if (response.data.success) {
        showNotification?.('Hiring request deleted successfully', 'success');
        fetchData();
      }
    } catch (error) {
      logger.error('Error deleting hiring request:', error);
      showNotification?.(error.response?.data?.error || 'Failed to delete hiring request', 'error');
    }
  };

  const deleteJobPosting = async (postingId) => {
    if (!window.confirm('Are you sure you want to delete this job posting?')) {
      return;
    }

    try {
      const response = await axiosInstance.delete(`/api/recruitment/job-postings/${postingId}`);
      if (response.data.success) {
        showNotification?.('Job posting deleted successfully', 'success');
        fetchData();
      }
    } catch (error) {
      logger.error('Error deleting job posting:', error);
      showNotification?.(error.response?.data?.error || 'Failed to delete job posting', 'error');
    }
  };

  const syncApplications = async (postingId) => {
    try {
      showNotification?.('Syncing applications...', 'info');
      const response = await axiosInstance.post(`/api/recruitment/job-postings/${postingId}/sync-applications`);
      if (response.data.success) {
        showNotification?.(`Synced ${response.data.data.syncedCount} new applications`, 'success');
        fetchData();
      }
    } catch (error) {
      logger.error('Error syncing applications:', error);
      showNotification?.('Failed to sync applications', 'error');
    }
  };

  const publishJobPosting = async (postingId) => {
    if (!window.confirm('Publish this job posting? It will be posted to all connected ATS platforms (BreezyHR, Workable, etc.).')) {
      return;
    }

    try {
      showNotification?.('Publishing and distributing job...', 'info');
      const response = await axiosInstance.patch(`/api/recruitment/job-postings/${postingId}/publish`);
      if (response.data.success) {
        const distribution = response.data.distribution || [];
        const successCount = distribution.filter(d => d.success).length;
        const failCount = distribution.filter(d => !d.success).length;

        if (distribution.length === 0) {
          showNotification?.('Job published (no ATS platforms connected)', 'info');
        } else if (failCount === 0) {
          showNotification?.(`Job published and posted to ${successCount} platform(s)!`, 'success');
        } else {
          // Show detailed error for failures
          const failedPlatforms = distribution.filter(d => !d.success);
          const errorDetails = failedPlatforms.map(d => `${d.platform}: ${d.error}`).join('; ');
          showNotification?.(`Job published but failed to post to ${failCount} platform(s). ${errorDetails}`, 'error');
          logger.error('Distribution failures:', failedPlatforms);
        }
        fetchData();
      }
    } catch (error) {
      logger.error('Error publishing job posting:', error);
      showNotification?.(error.response?.data?.error || 'Failed to publish job posting', 'error');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      active: 'bg-blue-100 text-blue-800',
      closed: 'bg-gray-100 text-gray-800',
      screening: 'bg-purple-100 text-purple-800',
      interviewing: 'bg-indigo-100 text-indigo-800',
      hired: 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatLocation = (location) => {
    if (!location) return 'N/A';
    if (typeof location === 'string') return location;

    // If location is an object with country, city, etc.
    const parts = [];
    if (location.city) parts.push(location.city);
    if (location.country) parts.push(location.country);

    let locationStr = parts.join(', ') || 'N/A';

    if (location.allowsRemote) {
      locationStr += ' (Remote)';
    }

    return locationStr;
  };

  const renderDashboard = () => (
    <div className="recruitment-dashboard">
      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon bg-blue-100 text-blue-600">
            <i className="fas fa-briefcase"></i>
          </div>
          <div className="stat-content">
            <h3>{stats.totalHiringRequests || 0}</h3>
            <p>Total Hiring Requests</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon bg-green-100 text-green-600">
            <i className="fas fa-newspaper"></i>
          </div>
          <div className="stat-content">
            <h3>{stats.activeJobPostings || 0}</h3>
            <p>Active Job Postings</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon bg-purple-100 text-purple-600">
            <i className="fas fa-users"></i>
          </div>
          <div className="stat-content">
            <h3>{stats.totalCandidates || 0}</h3>
            <p>Total Candidates</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon bg-orange-100 text-orange-600">
            <i className="fas fa-user-check"></i>
          </div>
          <div className="stat-content">
            <h3>{stats.candidatesInReview || 0}</h3>
            <p>In Review</p>
          </div>
        </div>
      </div>

      {/* Recent Hiring Requests */}
      <div className="section-card mt-6">
        <div className="section-header">
          <h2>Recent Hiring Requests</h2>
          <button
            className="btn-view-all"
            onClick={() => setActiveTab('hiring-requests')}
          >
            View All
          </button>
        </div>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Position</th>
                <th>Department</th>
                <th>Status</th>
                <th>HR Agent</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {hiringRequests.map(request => (
                <tr key={request._id}>
                  <td>
                    <div className="font-medium">{request.jobDetails?.jobTitle || 'N/A'}</div>
                    <div className="text-sm text-gray-500">{request.jobDetails?.seniority || ''}</div>
                  </td>
                  <td>{request.jobDetails?.department || 'N/A'}</td>
                  <td>
                    <span className={`status-badge ${getStatusColor(request.status)}`}>
                      {request.status}
                    </span>
                  </td>
                  <td>{request.assignedAgent?.agentName || 'Unassigned'}</td>
                  <td>{new Date(request.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderHiringRequests = () => {
    // Filter hiring requests based on search and status
    const filteredRequests = hiringRequests.filter(request => {
      const matchesSearch = !searchTerm ||
        request.jobDetails?.jobTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.jobDetails?.department?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = !statusFilter || request.status === statusFilter;

      return matchesSearch && matchesStatus;
    });

    return (
      <div className="section-card">
        <div className="section-header">
          <h2>All Hiring Requests</h2>
          <div className="filters">
            <div className="search-box">
              <MagnifyingGlassIcon className="search-icon" />
              <input
                type="text"
                placeholder="Search positions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select
              className="filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="active">Active</option>
              <option value="rejected">Rejected</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Position</th>
                <th>Department</th>
                <th>Location</th>
                <th>Type</th>
                <th>Status</th>
                <th>HR Agent</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRequests.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center text-gray-500 py-8">
                    No hiring requests found
                  </td>
                </tr>
              ) : (
                filteredRequests.map(request => (
                  <tr key={request._id}>
                    <td>
                      <div className="font-medium">{request.jobDetails?.jobTitle || 'N/A'}</div>
                      <div className="text-sm text-gray-500">{request.jobDetails?.seniority || ''}</div>
                    </td>
                    <td>{request.jobDetails?.department || 'N/A'}</td>
                    <td>{request.jobDetails?.location || 'N/A'}</td>
                    <td>
                      <span className="badge">{request.jobDetails?.employmentType?.replace('_', ' ') || 'N/A'}</span>
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusColor(request.status)}`}>
                        {request.status}
                      </span>
                    </td>
                    <td>{request.assignedAgent?.agentName || 'Unassigned'}</td>
                    <td>{new Date(request.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-action"
                          title="View Details"
                          onClick={() => viewHiringRequestDetails(request._id)}
                        >
                          <EyeIcon className="icon" />
                        </button>
                        <button
                          className="btn-action btn-danger"
                          title="Delete"
                          onClick={() => deleteHiringRequest(request._id)}
                        >
                          <TrashIcon className="icon" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderJobPostings = () => {
    // Filter job postings based on search and status
    const filteredPostings = jobPostings.filter(posting => {
      const matchesSearch = !searchTerm ||
        posting.jobTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        posting.location?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = !statusFilter || posting.status === statusFilter;

      return matchesSearch && matchesStatus;
    });

    return (
      <div className="section-card">
        <div className="section-header">
          <h2>Job Postings</h2>
          <div className="filters">
            <div className="search-box">
              <MagnifyingGlassIcon className="search-icon" />
              <input
                type="text"
                placeholder="Search jobs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select
              className="filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="pending_approval">Pending Approval</option>
              <option value="approved">Approved</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Job Title</th>
                <th>Status</th>
                <th>Distribution</th>
                <th>Applications</th>
                <th>Views</th>
                <th>Posted</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPostings.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center text-gray-500 py-8">
                    No job postings found
                  </td>
                </tr>
              ) : (
                filteredPostings.map(posting => (
                  <tr key={posting._id}>
                    <td>
                      <div className="font-medium">{posting.jobTitle || 'N/A'}</div>
                      <div className="text-sm text-gray-500">{formatLocation(posting.location)}</div>
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusColor(posting.status)}`}>
                        {posting.status}
                      </span>
                    </td>
                    <td>
                      <div className="distribution-badges">
                        {posting.distribution?.length > 0 ? (
                          posting.distribution.map((dist, idx) => (
                            <span key={idx} className={`platform-badge ${dist.status}`}>
                              {dist.platform}
                            </span>
                          ))
                        ) : (
                          <span className="text-gray-400 text-sm">Not posted</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="font-medium">{posting.analytics?.totalApplications || 0}</span>
                    </td>
                    <td>{posting.analytics?.totalViews || 0}</td>
                    <td>{posting.timeline?.postedAt ? new Date(posting.timeline.postedAt).toLocaleDateString() : (posting.createdAt ? new Date(posting.createdAt).toLocaleDateString() : 'N/A')}</td>
                    <td>
                      <div className="action-buttons">
                        {posting.status === 'draft' && (
                          <button
                            className="btn-action btn-primary"
                            title="Publish Job"
                            onClick={() => publishJobPosting(posting._id)}
                          >
                            <NewspaperIcon className="icon" />
                          </button>
                        )}
                        <button
                          className="btn-action"
                          title="View Details"
                          onClick={() => viewJobPostingDetails(posting._id)}
                        >
                          <EyeIcon className="icon" />
                        </button>
                        {posting.status !== 'draft' && (
                          <button
                            className="btn-action"
                            title="Sync Applications"
                            onClick={() => syncApplications(posting._id)}
                          >
                            <ArrowPathIcon className="icon" />
                          </button>
                        )}
                        <button
                          className="btn-action btn-danger"
                          title="Delete"
                          onClick={() => deleteJobPosting(posting._id)}
                        >
                          <TrashIcon className="icon" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderCandidates = () => (
    <div className="section-card">
      <div className="section-header">
        <h2>Candidates Pipeline</h2>
        <div className="filters">
          <select className="filter-select">
            <option value="">All Statuses</option>
            <option value="new">New</option>
            <option value="screening">Screening</option>
            <option value="interviewing">Interviewing</option>
            <option value="offered">Offered</option>
            <option value="hired">Hired</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="kanban-board">
        {['new', 'screening', 'interviewing', 'offered', 'hired'].map(status => {
          const statusCandidates = candidates.filter(c => c.currentStatus === status);
          const statusLabels = {
            new: 'New',
            screening: 'Screening',
            interviewing: 'Interviewing',
            offered: 'Offered',
            hired: 'Hired'
          };
          return (
            <div key={status} className="kanban-column">
              <div className="kanban-header">
                <h3>{statusLabels[status]}</h3>
                <span className="count">{statusCandidates.length}</span>
              </div>
              <div className="kanban-cards">
                {statusCandidates.length === 0 ? (
                  <div className="kanban-empty">
                    No candidates in this stage
                  </div>
                ) : (
                  statusCandidates.map(candidate => (
                    <div
                      key={candidate._id}
                      className="kanban-card"
                      onClick={() => viewCandidateDetails(candidate)}
                    >
                      <div className="candidate-header">
                        <h4>{candidate.personalInfo?.firstName || ''} {candidate.personalInfo?.lastName || 'N/A'}</h4>
                        {candidate.aiScreening?.overallScore && (
                          <span className={`score-badge ${
                            candidate.aiScreening.overallScore >= 70 ? 'high' :
                            candidate.aiScreening.overallScore >= 50 ? 'medium' : 'low'
                          }`}>
                            {candidate.aiScreening.overallScore}
                          </span>
                        )}
                      </div>
                      <div className="candidate-info">
                        <p className="text-sm">{candidate.jobPostingSnapshot?.jobTitle || 'N/A'}</p>
                        <p className="text-xs">
                          Applied {candidate.appliedAt ? new Date(candidate.appliedAt).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                      {candidate.aiScreening?.recommendation && (
                        <div className={`recommendation ${candidate.aiScreening.recommendation}`}>
                          {candidate.aiScreening.recommendation}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderCandidateModal = () => {
    if (!selectedCandidate) return null;

    return (
      <div className="modal-overlay" onClick={() => setShowCandidateModal(false)}>
        <div className="modal-content large" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>
              {selectedCandidate.personalInfo?.firstName || ''} {selectedCandidate.personalInfo?.lastName || 'N/A'}
            </h2>
            <button
              className="modal-close"
              onClick={() => setShowCandidateModal(false)}
            >
              <XMarkIcon className="icon" />
            </button>
          </div>

          <div className="modal-body">
            {/* Personal Info */}
            <div className="info-section">
              <h3>Contact Information</h3>
              <div className="info-grid">
                <div>
                  <label>Email</label>
                  <p>{selectedCandidate.personalInfo?.email || 'N/A'}</p>
                </div>
                <div>
                  <label>Phone</label>
                  <p>{selectedCandidate.personalInfo?.phone || 'N/A'}</p>
                </div>
                {selectedCandidate.personalInfo?.linkedInUrl && (
                  <div>
                    <label>LinkedIn</label>
                    <a href={selectedCandidate.personalInfo.linkedInUrl} target="_blank" rel="noopener noreferrer">
                      View Profile
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* AI Screening Results */}
            {selectedCandidate.aiScreening && (
              <div className="info-section">
                <h3>AI Screening Results</h3>
                <div className="screening-results">
                  <div className="score-display">
                    <div className="overall-score">
                      <span className="score-value">{selectedCandidate.aiScreening.overallScore}</span>
                      <span className="score-label">Overall Score</span>
                    </div>
                    <div className={`recommendation-badge ${selectedCandidate.aiScreening.recommendation}`}>
                      {selectedCandidate.aiScreening.recommendation}
                    </div>
                  </div>

                  <div className="score-breakdown">
                    <h4>Score Breakdown</h4>
                    {Object.entries(selectedCandidate.aiScreening.scoreBreakdown || {}).map(([key, value]) => (
                      <div key={key} className="score-item">
                        <label>{key.replace(/([A-Z])/g, ' $1').trim()}</label>
                        <div className="score-bar">
                          <div
                            className="score-fill"
                            style={{ width: `${value}%` }}
                          ></div>
                          <span>{value}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="analysis">
                    <h4>Analysis</h4>
                    <p>{selectedCandidate.aiScreening.analysis}</p>
                  </div>

                  {selectedCandidate.aiScreening.strengthsWeaknesses && (
                    <>
                      <div className="strengths">
                        <h4>Strengths</h4>
                        <ul>
                          {selectedCandidate.aiScreening.strengthsWeaknesses.strengths?.map((strength, idx) => (
                            <li key={idx}>{strength}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="weaknesses">
                        <h4>Areas for Improvement</h4>
                        <ul>
                          {selectedCandidate.aiScreening.strengthsWeaknesses.weaknesses?.map((weakness, idx) => (
                            <li key={idx}>{weakness}</li>
                          ))}
                        </ul>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Resume */}
            {selectedCandidate.resume?.parsedData && (
              <div className="info-section">
                <h3>Resume</h3>
                <div className="resume-content">
                  <pre>{JSON.stringify(selectedCandidate.resume.parsedData, null, 2)}</pre>
                </div>
                {selectedCandidate.resume.url && (
                  <a
                    href={selectedCandidate.resume.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-download"
                  >
                    <i className="fas fa-download"></i> Download Resume
                  </a>
                )}
              </div>
            )}

            {/* Timeline */}
            {selectedCandidate.timeline && selectedCandidate.timeline.length > 0 && (
              <div className="info-section">
                <h3>Activity Timeline</h3>
                <div className="timeline">
                  {selectedCandidate.timeline.map((event, idx) => (
                    <div key={idx} className="timeline-item">
                      <div className="timeline-marker"></div>
                      <div className="timeline-content">
                        <div className="timeline-header">
                          <span className="event-type">{event.eventType}</span>
                          <span className="event-date">
                            {new Date(event.timestamp).toLocaleString()}
                          </span>
                        </div>
                        {event.details && (
                          <p className="event-details">{event.details}</p>
                        )}
                        {event.performedBy && (
                          <p className="event-performer">
                            By: {event.performedBy.name}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              className="btn-secondary"
              onClick={() => setShowCandidateModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderHiringRequestModal = () => {
    if (!selectedHiringRequest) return null;

    return (
      <div className="modal-overlay" onClick={() => setShowHiringRequestModal(false)}>
        <div className="modal-content large" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{selectedHiringRequest.jobDetails?.jobTitle || 'Hiring Request'}</h2>
            <button
              className="modal-close"
              onClick={() => setShowHiringRequestModal(false)}
            >
              <XMarkIcon className="icon" />
            </button>
          </div>

          <div className="modal-body">
            <div className="info-section">
              <h3>Job Details</h3>
              <div className="info-grid">
                <div>
                  <label>Position</label>
                  <p>{selectedHiringRequest.jobDetails?.jobTitle || 'N/A'}</p>
                </div>
                <div>
                  <label>Seniority</label>
                  <p>{selectedHiringRequest.jobDetails?.seniority || 'N/A'}</p>
                </div>
                <div>
                  <label>Department</label>
                  <p>{selectedHiringRequest.jobDetails?.department || 'N/A'}</p>
                </div>
                <div>
                  <label>Employment Type</label>
                  <p>{selectedHiringRequest.jobDetails?.employmentType?.replace('_', ' ') || 'N/A'}</p>
                </div>
                <div>
                  <label>Location</label>
                  <p>{selectedHiringRequest.jobDetails?.location || 'N/A'}</p>
                </div>
                <div>
                  <label>Number of Positions</label>
                  <p>{selectedHiringRequest.jobDetails?.numberOfPositions || 1}</p>
                </div>
              </div>
            </div>

            {selectedHiringRequest.jobDetails?.salaryRange && (
              <div className="info-section">
                <h3>Salary Range</h3>
                <p>{selectedHiringRequest.jobDetails.salaryRange.min} - {selectedHiringRequest.jobDetails.salaryRange.max} {selectedHiringRequest.jobDetails.salaryRange.currency}</p>
              </div>
            )}

            {selectedHiringRequest.jobDetails?.requiredSkills && selectedHiringRequest.jobDetails.requiredSkills.length > 0 && (
              <div className="info-section">
                <h3>Required Skills</h3>
                <div className="skills-list">
                  {selectedHiringRequest.jobDetails.requiredSkills.map((skill, idx) => (
                    <span key={idx} className="skill-badge">{skill}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="info-section">
              <h3>Status Information</h3>
              <div className="info-grid">
                <div>
                  <label>Current Status</label>
                  <span className={`status-badge ${getStatusColor(selectedHiringRequest.status)}`}>
                    {selectedHiringRequest.status}
                  </span>
                </div>
                <div>
                  <label>Stage</label>
                  <p>{selectedHiringRequest.stage || 'N/A'}</p>
                </div>
                <div>
                  <label>HR Agent</label>
                  <p>{selectedHiringRequest.assignedAgent?.agentName || 'Unassigned'}</p>
                </div>
                <div>
                  <label>Created</label>
                  <p>{new Date(selectedHiringRequest.createdAt).toLocaleString()}</p>
                </div>
              </div>
            </div>

            {selectedHiringRequest.stageHistory && selectedHiringRequest.stageHistory.length > 0 && (
              <div className="info-section">
                <h3>Stage History</h3>
                <div className="timeline">
                  {selectedHiringRequest.stageHistory.map((stage, idx) => (
                    <div key={idx} className="timeline-item">
                      <div className="timeline-marker"></div>
                      <div className="timeline-content">
                        <div className="timeline-header">
                          <span className="event-type">{stage.stage}</span>
                          <span className="event-date">
                            {new Date(stage.timestamp).toLocaleString()}
                          </span>
                        </div>
                        {stage.notes && (
                          <p className="event-details">{stage.notes}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              className="btn-secondary"
              onClick={() => setShowHiringRequestModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderJobPostingModal = () => {
    if (!selectedJobPosting) return null;

    return (
      <div className="modal-overlay" onClick={() => setShowJobPostingModal(false)}>
        <div className="modal-content large" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{selectedJobPosting.jobTitle || 'Job Posting'}</h2>
            <button
              className="modal-close"
              onClick={() => setShowJobPostingModal(false)}
            >
              <XMarkIcon className="icon" />
            </button>
          </div>

          <div className="modal-body">
            <div className="info-section">
              <h3>Job Information</h3>
              <div className="info-grid">
                <div>
                  <label>Title</label>
                  <p>{selectedJobPosting.jobTitle || 'N/A'}</p>
                </div>
                <div>
                  <label>Status</label>
                  <span className={`status-badge ${getStatusColor(selectedJobPosting.status)}`}>
                    {selectedJobPosting.status}
                  </span>
                </div>
                <div>
                  <label>Employment Type</label>
                  <p>{selectedJobPosting.employmentType || 'N/A'}</p>
                </div>
                <div>
                  <label>Location</label>
                  <p>{formatLocation(selectedJobPosting.location)}</p>
                </div>
              </div>
            </div>

            {selectedJobPosting.description?.fullDescription && (
              <div className="info-section">
                <h3>Description</h3>
                <p>{selectedJobPosting.description.fullDescription}</p>
              </div>
            )}

            {selectedJobPosting.description?.responsibilities && selectedJobPosting.description.responsibilities.length > 0 && (
              <div className="info-section">
                <h3>Responsibilities</h3>
                <ul>
                  {selectedJobPosting.description.responsibilities.map((resp, idx) => (
                    <li key={idx}>{resp}</li>
                  ))}
                </ul>
              </div>
            )}

            {selectedJobPosting.description?.requirements?.mustHave && selectedJobPosting.description.requirements.mustHave.length > 0 && (
              <div className="info-section">
                <h3>Required Qualifications</h3>
                <ul>
                  {selectedJobPosting.description.requirements.mustHave.map((req, idx) => (
                    <li key={idx}>{req}</li>
                  ))}
                </ul>
              </div>
            )}

            {selectedJobPosting.distribution && selectedJobPosting.distribution.length > 0 && (
              <div className="info-section">
                <h3>Distribution Platforms</h3>
                <div className="distribution-grid">
                  {selectedJobPosting.distribution.map((dist, idx) => (
                    <div key={idx} className="distribution-card">
                      <div className="distribution-header">
                        <span className="platform-name">{dist.platform}</span>
                        <span className={`status-badge ${getStatusColor(dist.status)}`}>
                          {dist.status}
                        </span>
                      </div>
                      {dist.postingUrl && (
                        <a href={dist.postingUrl} target="_blank" rel="noopener noreferrer" className="posting-link">
                          <i className="fas fa-external-link-alt"></i> View Posting
                        </a>
                      )}
                      {dist.lastSyncedAt && (
                        <p className="text-sm text-gray-500">
                          Last synced: {new Date(dist.lastSyncedAt).toLocaleString()}
                        </p>
                      )}
                      <p className="text-sm">Applications: {dist.applicationCount || 0}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="info-section">
              <h3>Analytics</h3>
              <div className="stats-grid-small">
                <div className="stat-card-small">
                  <div className="stat-value">{selectedJobPosting.analytics?.totalApplications || 0}</div>
                  <div className="stat-label">Total Applications</div>
                </div>
                <div className="stat-card-small">
                  <div className="stat-value">{selectedJobPosting.analytics?.totalViews || 0}</div>
                  <div className="stat-label">Total Views</div>
                </div>
                <div className="stat-card-small">
                  <div className="stat-value">{selectedJobPosting.analytics?.clickThroughRate || 0}%</div>
                  <div className="stat-label">Click-Through Rate</div>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              className="btn-primary"
              onClick={() => syncApplications(selectedJobPosting._id)}
            >
              <ArrowPathIcon className="icon" /> Sync Applications
            </button>
            <button
              className="btn-secondary"
              onClick={() => setShowJobPostingModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="ai-recruitment">
      <div className="page-header">
        <h1>AI Recruitment</h1>
        <p>Autonomous hiring powered by AI HR agents</p>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('dashboard');
            setSearchTerm('');
            setStatusFilter('');
          }}
        >
          <ChartBarIcon className="tab-icon" /> Dashboard
        </button>
        <button
          className={`tab ${activeTab === 'hiring-requests' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('hiring-requests');
            setSearchTerm('');
            setStatusFilter('');
          }}
        >
          <BriefcaseIcon className="tab-icon" /> Hiring Requests
        </button>
        <button
          className={`tab ${activeTab === 'job-postings' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('job-postings');
            setSearchTerm('');
            setStatusFilter('');
          }}
        >
          <NewspaperIcon className="tab-icon" /> Job Postings
        </button>
        <button
          className={`tab ${activeTab === 'candidates' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('candidates');
            setSearchTerm('');
            setStatusFilter('');
          }}
        >
          <UsersIcon className="tab-icon" /> Candidates
        </button>
      </div>

      {/* Content */}
      <div className="tab-content">
        {loading ? (
          <div className="loading-state">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Loading recruitment data...</p>
          </div>
        ) : (
          <>
            {activeTab === 'dashboard' && renderDashboard()}
            {activeTab === 'hiring-requests' && renderHiringRequests()}
            {activeTab === 'job-postings' && renderJobPostings()}
            {activeTab === 'candidates' && renderCandidates()}
          </>
        )}
      </div>

      {/* Modals */}
      {showCandidateModal && renderCandidateModal()}
      {showHiringRequestModal && renderHiringRequestModal()}
      {showJobPostingModal && renderJobPostingModal()}
    </div>
  );
};

export default AIRecruitment;
